import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner, Notice } from '../ui/controls'
import { Band } from '../ui/Band'
import type { MessageKey } from '../i18n/messages'
import type { MatchHistoryEntry } from '../api/types'

/**
 * 「我的匹配」— every pair this user has paid for, in all nine states (决议 V1.2 §4.6).
 *
 * Terminal entries are never hidden or removed: 用户付过钱，有权保留记录. A declined invitation
 * from six months ago is still something someone paid twenty dollars to learn.
 */
export function MatchesPage() {
  const { t } = useI18n()

  const [items, setItems] = useState<MatchHistoryEntry[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    api.matching.history().then(setItems).catch(setError)
  }, [])

  if (!items) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  return (
    <div className="page stack">
      <h1>{t('matches.title')}</h1>

      <ErrorBanner error={error} />

      {items.length === 0 ? (
        <Notice>{t('matches.empty')}</Notice>
      ) : (
        <>
          <p className="field-hint">{t('matches.kept')}</p>

          <ul className="match-list">
            {items.map((entry) => (
              <li className="card match-row" key={entry.matchId}>
                {entry.primaryPhotoId ? (
                  <img className="match-thumb" src={api.photo.url(entry.primaryPhotoId)} alt="" />
                ) : (
                  <div className="match-thumb match-thumb-empty" />
                )}

                <div className="stack match-body">
                  <strong>{entry.displayName}</strong>

                  <div className="row-links">
                    <Band value={entry.overallBand} />
                    <span className={`state state-${entry.state.toLowerCase()}`}>
                      {t(`state.${entry.state}` as MessageKey)}
                    </span>
                  </div>

                  <span className="field-hint">
                    {t('matches.unlockedAt')} {new Date(entry.unlockedAt).toLocaleDateString()}
                  </span>

                  <div className="row-links">
                    <Link to={`/matches/${entry.matchId}`}>{t('matches.viewReport')}</Link>

                    {/*
                      Only CancelledByMe carries this: withdrawing is the one terminal state a new
                      invitation may start from, and it is free (邀约与商务参数决议 §2.4).
                    */}
                    {entry.canReInvite ? (
                      <Link to={`/matches/${entry.matchId}?reinvite=1`}>{t('matches.reInvite')}</Link>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
